import { useMemo } from 'react'
import kebabCase from 'lodash/kebabCase'
import { TppCell, TppMatrixState, TppRow } from '../types'

type TppViewModel = {
    columnOptions: { id: string; label: string }[]
    sectionOptions: { id: string; label: string }[]
    rowOptions: { id: string; label: string }[]
    cellLookupForView: Record<string, Record<string, TppCell>>
    availableColumnIdsForView: string[]
    visibleColumnIds: string[]
    filteredSectionIdsWithRows: string[]
    sectionRowIds: Record<string, string[]>
    rowMapForView: Record<string, TppRow>
    pinnedColumnIdsForView: string[]
    pinnedColumnIndexById: Record<string, number>
    tabsData: string[]
}

type HiddenFilters = {
    hiddenColumnIds: string[]
    hiddenRowIds: string[]
    hiddenSectionIds: string[]
}

const EMPTY_VIEW_MODEL: TppViewModel = {
    columnOptions: [],
    sectionOptions: [],
    rowOptions: [],
    cellLookupForView: {},
    availableColumnIdsForView: [],
    visibleColumnIds: [],
    filteredSectionIdsWithRows: [],
    sectionRowIds: {},
    rowMapForView: {},
    pinnedColumnIdsForView: [],
    pinnedColumnIndexById: {},
    tabsData: [],
}

const buildTppViewModel = (
    matrixState: TppMatrixState | null,
    viewMode: 'all' | 'summary',
    selectedTabId: string | undefined,
    filters: HiddenFilters
): TppViewModel => {
    if (!matrixState?.tpp) return EMPTY_VIEW_MODEL

    const hiddenColumns = new Set(filters.hiddenColumnIds)
    const hiddenRows = new Set(filters.hiddenRowIds)
    const hiddenSections = new Set(filters.hiddenSectionIds)

    const columnOptions = matrixState.tpp.columnIds.map((columnId) => ({
        id: columnId,
        label: matrixState.columns[columnId]?.title || columnId,
    }))
    const sectionOptions = matrixState.tpp.sectionIds.map((sectionId) => ({
        id: sectionId,
        label: matrixState.sections[sectionId]?.title || sectionId,
    }))
    const rowOptions = matrixState.tpp.sectionIds.flatMap((sectionId) => {
        const section = matrixState.sections[sectionId]
        return section.rowIds.map((rowId) => ({
            id: rowId,
            label: `${section.title} — ${matrixState.rows[rowId]?.title || rowId}`,
        }))
    })

    const archivedColumnIds = matrixState.tpp.columnIds.filter((columnId) => !!matrixState.columns[columnId]?.archived)
    const nonArchivedColumnIds = matrixState.tpp.columnIds.filter(
        (columnId) => !matrixState.columns[columnId]?.archived
    )
    const availableNonArchivedColumnIds = nonArchivedColumnIds.filter((columnId) => !hiddenColumns.has(columnId))

    const baseSectionRowIds = matrixState.tpp.sectionIds.reduce<Record<string, string[]>>((acc, sectionId) => {
        const section = matrixState.sections[sectionId]
        acc[sectionId] = section.rowIds.filter((rowId) => !hiddenRows.has(rowId))
        return acc
    }, {})

    const summary =
        viewMode === 'summary'
            ? buildSummaryView(matrixState, hiddenRows, hiddenSections, hiddenColumns, new Set(archivedColumnIds))
            : null

    const cellLookupForView = summary ? summary.cellLookup : matrixState.cellLookup
    const availableNonArchivedColumnIdsForView = summary
        ? availableNonArchivedColumnIds.filter((columnId) => summary.columnIds.has(columnId))
        : availableNonArchivedColumnIds
    const archivedColumnIdsForView = summary
        ? archivedColumnIds.filter((columnId) => summary.columnIds.has(columnId))
        : archivedColumnIds

    const sectionRowIds = summary ? filterSectionRowIds(baseSectionRowIds, summary.rowIds) : baseSectionRowIds
    const filteredSectionIdsWithRows = filterSections(matrixState, hiddenSections, sectionRowIds, summary?.sectionIds)
    const rowMapForView = summary ? filterRowMap(matrixState.rows, summary.slotIds) : matrixState.rows

    const visibleColumnIds = getVisibleColumnsForTab(
        matrixState,
        availableNonArchivedColumnIdsForView,
        archivedColumnIdsForView,
        selectedTabId
    )
    const pinnedColumnIdsForView = getPinnedColumns(matrixState, visibleColumnIds)
    const pinnedColumnIdsForTabs = getPinnedColumns(matrixState, availableNonArchivedColumnIdsForView)
    const orderedVisibleColumnIds = sortPinnedColumnsFirst(visibleColumnIds, pinnedColumnIdsForView)
    const orderedAvailableColumnIdsForTabs = sortPinnedColumnsFirst(
        availableNonArchivedColumnIdsForView,
        pinnedColumnIdsForTabs
    )
    const pinnedColumnIndexById = indexById(pinnedColumnIdsForView)

    const tabsData = [
        'All',
        ...orderedAvailableColumnIdsForTabs.map((id) => matrixState.columns[id].title),
        ...(viewMode !== 'summary' && archivedColumnIds.length > 0 ? ['Archived'] : []),
    ]

    return {
        columnOptions,
        sectionOptions,
        rowOptions,
        cellLookupForView,
        availableColumnIdsForView: availableNonArchivedColumnIdsForView,
        visibleColumnIds: orderedVisibleColumnIds,
        filteredSectionIdsWithRows,
        sectionRowIds,
        rowMapForView,
        pinnedColumnIdsForView,
        pinnedColumnIndexById,
        tabsData,
    }
}

const buildSummaryView = (
    matrixState: TppMatrixState,
    hiddenRows: Set<string>,
    hiddenSections: Set<string>,
    hiddenColumns: Set<string>,
    archivedColumns: Set<string>
) => {
    const columnIds = new Set<string>()
    const rowIds = new Set<string>()
    const sectionIds = new Set<string>()
    const slotIds = new Set<string>()
    const cellLookup: Record<string, Record<string, TppCell>> = {}

    const eligibleColumns = new Set(
        matrixState.tpp.columnIds.filter((columnId) => !hiddenColumns.has(columnId) && !archivedColumns.has(columnId))
    )

    Object.entries(matrixState.cellLookup).forEach(([slotId, columns]) => {
        const rowId = matrixState.slots[slotId]?.rowId
        const sectionId = rowId ? matrixState.rows[rowId]?.sectionId : undefined
        if ((rowId && hiddenRows.has(rowId)) || (sectionId && hiddenSections.has(sectionId))) return

        const hasHighlighted = Object.values(columns).some(
            (cell) => cell.isHighlighted && eligibleColumns.has(cell.columnId)
        )
        if (!hasHighlighted) return

        Object.values(columns).forEach((cell) => {
            if (!eligibleColumns.has(cell.columnId)) return
            if (!cellLookup[slotId]) cellLookup[slotId] = {}
            cellLookup[slotId][cell.columnId] = cell
            columnIds.add(cell.columnId)
        })

        slotIds.add(slotId)
        if (rowId) rowIds.add(rowId)
        if (sectionId) sectionIds.add(sectionId)
    })

    return { cellLookup, columnIds, rowIds, sectionIds, slotIds }
}

const filterSectionRowIds = (sectionRowIds: Record<string, string[]>, keepRowIds: Set<string>) =>
    Object.entries(sectionRowIds).reduce<Record<string, string[]>>((acc, [sectionId, rowIds]) => {
        acc[sectionId] = rowIds.filter((rowId) => keepRowIds.has(rowId))
        return acc
    }, {})

const filterSections = (
    matrixState: TppMatrixState,
    hiddenSections: Set<string>,
    sectionRowIds: Record<string, string[]>,
    visibleSectionIds?: Set<string>
) =>
    matrixState.tpp.sectionIds
        .filter((sectionId) => !hiddenSections.has(sectionId))
        .filter((sectionId) => (sectionRowIds[sectionId] || []).length > 0)
        .filter((sectionId) => (visibleSectionIds ? visibleSectionIds.has(sectionId) : true))

const filterRowMap = (rows: Record<string, TppRow>, keepSlotIds: Set<string>) =>
    Object.values(rows).reduce<Record<string, TppRow>>((acc, row) => {
        acc[row._id] = {
            ...row,
            slotIds: row.slotIds.filter((slotId) => keepSlotIds.has(slotId)),
        }
        return acc
    }, {})

const getVisibleColumnsForTab = (
    matrixState: TppMatrixState,
    nonArchivedColumnIds: string[],
    archivedColumnIds: string[],
    selectedTabId: string | undefined
) => {
    if (selectedTabId === 'archived') return archivedColumnIds
    if (!selectedTabId || selectedTabId === 'all') return nonArchivedColumnIds
    const matchId = nonArchivedColumnIds.find(
        (columnId) => kebabCase(matrixState.columns[columnId]?.title || '') === selectedTabId
    )
    return matchId ? [matchId] : nonArchivedColumnIds
}

const getPinnedColumns = (matrixState: TppMatrixState, visibleColumnIds: string[]) => {
    const visibleColumns = new Set(visibleColumnIds)
    return matrixState.pinnedColumnIds.filter((columnId) => visibleColumns.has(columnId))
}

const sortPinnedColumnsFirst = (columnIds: string[], pinnedColumnIds: string[]) => {
    if (!pinnedColumnIds.length) return columnIds
    const pinnedSet = new Set(pinnedColumnIds)
    return [...columnIds.filter((id) => pinnedSet.has(id)), ...columnIds.filter((id) => !pinnedSet.has(id))]
}

const indexById = (ids: string[]) =>
    ids.reduce<Record<string, number>>((acc, id, index) => {
        acc[id] = index
        return acc
    }, {})

export const useTppViewModel = (
    matrixState: TppMatrixState | null,
    viewMode: 'all' | 'summary',
    selectedTabId: string | undefined,
    filters: HiddenFilters
) =>
    useMemo(
        () => buildTppViewModel(matrixState, viewMode, selectedTabId, filters),
        [filters, matrixState, selectedTabId, viewMode]
    )
